import axios from 'axios';
import { getApiBaseUrl } from '../config/api';

export interface VaccinationSchedule {
  id: string;
  birdId: string;
  bird?: {
    bandId: string;
    species: { name: string };
  };
  vaccineName: string;
  vaccineType: 'ROUTINE' | 'EMERGENCY' | 'BOOSTER' | 'SEASONAL' | 'PREVENTIVE';
  vaccinationStatus: 'SCHEDULED' | 'ADMINISTERED' | 'OVERDUE' | 'CANCELLED' | 'POSTPONED';
  scheduledDate: string;
  administeredDate?: string;
  veterinarian?: string;
  clinicName?: string;
  batchNumber?: string;
  manufacturer?: string;
  expiryDate?: string;
  dosage?: string;
  administrationRoute?: string;
  nextDueDate?: string;
  reminderSent: boolean;
  sideEffects?: string;
  notes?: string;
  cost?: number;
  createdAt: string;
  updatedAt: string;
}

export interface CreateVaccinationScheduleDto {
  birdId: string;
  vaccineName: string;
  vaccineType: 'ROUTINE' | 'EMERGENCY' | 'BOOSTER' | 'SEASONAL' | 'PREVENTIVE';
  scheduledDate: string;
  veterinarian?: string;
  clinicName?: string;
  dosage?: string;
  administrationRoute?: string;
  notes?: string;
  cost?: number;
}

export interface UpdateVaccinationScheduleDto {
  vaccinationStatus?: 'SCHEDULED' | 'ADMINISTERED' | 'OVERDUE' | 'CANCELLED' | 'POSTPONED';
  administeredDate?: string;
  veterinarian?: string;
  clinicName?: string;
  batchNumber?: string;
  manufacturer?: string;
  expiryDate?: string;
  sideEffects?: string;
  notes?: string;
  cost?: number;
}

export interface VaccinationSummary {
  totalScheduled: number;
  totalAdministered: number;
  totalOverdue: number;
  upcomingVaccinations: VaccinationSchedule[];
  overdueVaccinations: VaccinationSchedule[];
  recentVaccinations: VaccinationSchedule[];
}

class VaccinationApi {
  private getAuthHeaders() {
    const token = localStorage.getItem('token');
    return {
      Authorization: token ? `Bearer ${token}` : '',
      'Content-Type': 'application/json',
    };
  }

  async getAll(birdId?: string): Promise<VaccinationSchedule[]> {
    const response = await axios.get(`${getApiBaseUrl()}/health/vaccinations`, {
      headers: this.getAuthHeaders(),
      params: birdId ? { birdId } : {},
    });
    return response.data;
  }

  async getById(id: string): Promise<VaccinationSchedule> {
    const response = await axios.get(`${getApiBaseUrl()}/health/vaccinations/${id}`, {
      headers: this.getAuthHeaders(),
    });
    return response.data;
  }

  async create(data: CreateVaccinationScheduleDto): Promise<VaccinationSchedule> {
    const response = await axios.post(`${getApiBaseUrl()}/health/vaccinations`, data, {
      headers: this.getAuthHeaders(),
    });
    return response.data;
  }

  async update(id: string, data: UpdateVaccinationScheduleDto): Promise<VaccinationSchedule> {
    const response = await axios.put(`${getApiBaseUrl()}/health/vaccinations/${id}`, data, {
      headers: this.getAuthHeaders(),
    });
    return response.data;
  }

  async delete(id: string): Promise<void> {
    await axios.delete(`${getApiBaseUrl()}/health/vaccinations/${id}`, {
      headers: this.getAuthHeaders(),
    });
  }

  async getUpcoming(days: number = 30): Promise<VaccinationSchedule[]> {
    const response = await axios.get(`${getApiBaseUrl()}/health/vaccinations/upcoming`, {
      headers: this.getAuthHeaders(),
      params: { days },
    });
    return response.data;
  }

  async getOverdue(): Promise<VaccinationSchedule[]> {
    const response = await axios.get(`${getApiBaseUrl()}/health/vaccinations/overdue`, {
      headers: this.getAuthHeaders(),
    });
    return response.data;
  }

  async getSummary(): Promise<VaccinationSummary> {
    const response = await axios.get(`${getApiBaseUrl()}/health/vaccinations/summary`, {
      headers: this.getAuthHeaders(),
    });
    return response.data;
  }

  async administer(id: string, data: {
    administeredDate: string;
    veterinarian?: string;
    clinicName?: string;
    batchNumber?: string;
    manufacturer?: string;
    sideEffects?: string;
    notes?: string;
  }): Promise<VaccinationSchedule> {
    const response = await axios.post(`${getApiBaseUrl()}/health/vaccinations/${id}/administer`, data, {
      headers: this.getAuthHeaders(),
    });
    return response.data;
  }

  async cancel(id: string, reason?: string): Promise<VaccinationSchedule> {
    const response = await axios.put(`${getApiBaseUrl()}/health/vaccinations/${id}`, {
      vaccinationStatus: 'CANCELLED',
      notes: reason,
    }, {
      headers: this.getAuthHeaders(),
    });
    return response.data;
  }

  async postpone(id: string, newDate: string, reason?: string): Promise<VaccinationSchedule> {
    const response = await axios.put(`${getApiBaseUrl()}/health/vaccinations/${id}`, {
      vaccinationStatus: 'POSTPONED',
      scheduledDate: newDate,
      notes: reason,
    }, {
      headers: this.getAuthHeaders(),
    });
    return response.data;
  }
}

export const vaccinationApi = new VaccinationApi(); 